import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Badge, Button, Form, Modal, Nav, Spinner, Tab } from 'react-bootstrap'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { URL as BACKEND_URL } from '../../../gql/client'
import { STORAGE_NAMES } from '../../../helpers/consts'
import { parseId } from '../../../helpers'
import { LeadTagManager } from '../../leads/components/LeadTagManager'
import { DrawerEditLead } from '../../leads/components/DrawerEditLead'
import { IInfoLead } from '../../leads/leadTypes'
import { getLeadSourceIcon } from '../../leads/helpers/leadSourceIcon'
import { LovableIcon } from '../../../components/icons/LovableIcon'

interface Props {
  isOpen: boolean
  onClose: () => void
  stage: { id: string; name: string }
  configId?: number | null
  onChanged?: () => void
  /** Sin `change_lovable_leads_webhook` el modal queda en solo lectura. */
  canEdit: boolean
}

interface TagOption {
  id: string
  name: string
  color: string
}

interface WebhookConfig {
  id: number
  token: string
  webhook_secret: string
  webhook_url: string
  is_active: boolean
  description: string
  type_lead: 'PERSONAL' | 'COMERCIAL'
  lead_source_id: string | null
  lead_source_name: string | null
  campus_id: string | null
  campus_name: string | null
  name_field: string
  email_field: string
  phone_field: string
  metadata_field_map: Record<string, string>
  tags: TagOption[]
  last_triggered_at: string | null
  last_lead_id: number | null
  leads_count: number
}

interface LeadSourceCategory {
  id: string
  name: string
  sources: { id: string; name: string }[]
}

interface Options {
  categories: LeadSourceCategory[]
  campuses: { id: string; name: string }[]
  tags: TagOption[]
}

type CopyField = 'url' | 'secret' | 'prompt' | null

interface MetadataRow {
  target: string
  source: string
}

function metadataMapToRows(map: Record<string, string> | undefined): MetadataRow[] {
  if (!map || Object.keys(map).length === 0) return []
  return Object.entries(map).map(([target, source]) => ({ target, source }))
}

function metadataRowsToMap(rows: MetadataRow[]): Record<string, string> {
  const map: Record<string, string> = {}
  rows.forEach(({ target, source }) => {
    const t = target.trim()
    const s = source.trim()
    if (t && s) map[t] = s
  })
  return map
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

function buildPrompt(params: {
  webhookUrl: string
  webhookSecret: string
  nameField: string
  emailField: string
  phoneField: string
  metadataRows: MetadataRow[]
}): string {
  const { webhookUrl, webhookSecret, nameField, emailField, phoneField, metadataRows } = params
  const extraKeys = metadataRows
    .filter((r) => r.target.trim() && r.source.trim())
    .map((r) => `  "${r.source.trim()}": "..."`)
  const sampleFields = [
    `  "${nameField || 'name'}": "..."`,
    `  "${emailField || 'email'}": "..."`,
    `  "${phoneField || 'phone'}": "..."`,
    ...extraKeys,
    `  "webhook_secret": "${webhookSecret}"`,
  ].join(',\n')

  return [
    'When the lead capture form on this site is submitted, send a POST request to:',
    webhookUrl,
    '',
    'With header:',
    'Content-Type: application/json',
    '',
    'And a JSON body shaped like this (use the actual form values in place of "..."):',
    '{',
    sampleFields,
    '}',
    '',
    `Send exactly these keys — they are configured to match this webhook's field mapping.`,
  ].join('\n')
}

const emdash = '—'

const wM = defineMessages({
  tabWebhook: { id: 'leads.webhook.tabWebhook', defaultMessage: 'Webhook' },
  tabFieldMapping: { id: 'leads.webhook.fieldMapping', defaultMessage: 'Field mapping' },
  tabExtraFields: { id: 'leads.webhook.tabExtraFields', defaultMessage: 'Extra Field Mapping' },
  tabPromptDetails: { id: 'leads.webhook.lovableTabPrompt', defaultMessage: 'Prompt Details' },
  configured: { id: 'leads.webhook.configured', defaultMessage: 'Webhook configured' },
  statusActive: { id: 'common.status.active', defaultMessage: 'Active' },
  statusInactive: { id: 'common.status.inactive', defaultMessage: 'Inactive' },
  colLeadFields: { id: 'leads.webhook.colLeadFields', defaultMessage: 'Lead Fields' },
  colOther: { id: 'leads.webhook.colOther', defaultMessage: 'Other' },
  extraMetadata: { id: 'leads.webhook.extraMetadata', defaultMessage: 'Additional metadata (optional)' },
  extraMetadataHelpLovable: {
    id: 'leads.webhook.extraMetadataHelpLovable',
    defaultMessage: 'Add here any extra data your Lovable form will send (utm_source, campaign, custom questions, etc.). In <b>Payload key</b> write exactly the JSON key your Lovable form will use for that value.',
  },
  fieldsConfigured: {
    id: 'leads.webhook.fieldsConfigured',
    defaultMessage: '{configured} / {total, plural, one {# field} other {# fields}} configured',
  },
  fieldsIncomplete: { id: 'leads.webhook.fieldsIncomplete', defaultMessage: '{count} incomplete' },
  extraWarning: {
    id: 'leads.webhook.extraWarning',
    defaultMessage: 'Avoid compound fields like <code>"Campaign Id : Campaign Name"</code> — split them into two separate fields. Also avoid concatenating multiple questions into a single field (e.g. <code>meta_data</code>); each question must have its own field so it is stored in a structured way.',
  },
  addField: { id: 'leads.webhook.addField', defaultMessage: 'Add field' },
  removeField: { id: 'leads.webhook.removeField', defaultMessage: 'Remove' },
  internalNamePlaceholder: {
    id: 'leads.webhook.internalNamePlaceholder',
    defaultMessage: 'Internal name (e.g. campaign_id)',
  },
  payloadKeyPlaceholder: {
    id: 'leads.webhook.payloadKeyPlaceholder',
    defaultMessage: 'Payload key (e.g. campaign_id)',
  },
  readOnly: {
    id: 'leads.webhook.readOnly',
    defaultMessage: 'You can view this webhook but not change it. Editing requires the change permission for this integration.',
  },
  lovableTitle: { id: 'leads.webhook.lovableTitle', defaultMessage: 'Lovable Webhooks' },
  lovableInstructions: {
    id: 'leads.webhook.lovableInstructions',
    defaultMessage: 'This webhook is for a lead-capture form <b>you build yourself in Lovable</b>. Go to the <b>Prompt Details</b> tab, copy the prompt, and paste it into your Lovable chat so its AI wires up the POST request for you.',
  },
  webhookUrl: { id: 'leads.webhook.webhookUrl', defaultMessage: 'Webhook URL' },
  webhookSecret: { id: 'leads.webhook.webhookSecret', defaultMessage: 'Webhook Secret' },
  lovableSecretHint: {
    id: 'leads.webhook.lovableSecretHint',
    defaultMessage: 'Send this value in the <code>webhook_secret</code> field of the JSON body — the prompt below already includes it.',
  },
  leadsReceived: { id: 'leads.webhook.leadsReceived', defaultMessage: 'Leads received' },
  lastTriggered: { id: 'leads.webhook.lastTriggered', defaultMessage: 'Last triggered' },
  viewLastLead: { id: 'leads.webhook.viewLastLead', defaultMessage: 'View last lead' },
  statusLabel: { id: 'common.field.status', defaultMessage: 'Status' },
  description: { id: 'leads.webhook.description', defaultMessage: 'Description' },
  descriptionPlaceholder: { id: 'leads.webhook.descriptionPlaceholder', defaultMessage: 'e.g. Summer campaign — Miami campus' },
  fieldMappingDescLovable: {
    id: 'leads.webhook.fieldMappingDescLovable',
    defaultMessage: 'Declare which JSON key your Lovable form will send for each lead field — there is no fixed contract, since you control the form.',
  },
  updateSettings: { id: 'leads.webhook.updateSettings', defaultMessage: 'Update settings' },
  createForStage: { id: 'leads.webhook.createForStage', defaultMessage: 'Create webhook for this stage' },
  leadSourceCategory: { id: 'leads.webhook.leadSourceCategory', defaultMessage: 'Lead Source Category' },
  selectCategory: { id: 'leads.webhook.selectCategory', defaultMessage: '— Select category —' },
  selectSource: { id: 'leads.webhook.selectSource', defaultMessage: '— Select source —' },
  campus: { id: 'leads.webhook.campus', defaultMessage: 'Campus' },
  selectCampus: { id: 'leads.webhook.selectCampus', defaultMessage: '— Select campus —' },
  leadType: { id: 'leads.webhook.leadType', defaultMessage: 'Lead type' },
  autoAssignTags: { id: 'leads.webhook.autoAssignTags', defaultMessage: 'Auto-assign tags' },
  manageTags: { id: 'leads.webhook.manageTags', defaultMessage: 'Manage tags' },
  tagsSelected: { id: 'leads.webhook.tagsSelected', defaultMessage: '{count, plural, one {# tag selected — applied automatically to every lead from this webhook} other {# tags selected — applied automatically to every lead from this webhook}}' },
  webhookActive: { id: 'leads.webhook.webhookActive', defaultMessage: 'Webhook active' },
  dangerZone: { id: 'leads.webhook.dangerZone', defaultMessage: 'Danger zone' },
  regenTokenSecret: { id: 'leads.webhook.regenTokenSecret', defaultMessage: 'Regenerate token & secret' },
  regenWarnLovable: {
    id: 'leads.webhook.regenWarnLovable',
    defaultMessage: 'This invalidates the current URL and secret. Update your Lovable prompt/site.',
  },
  confirmRegenerate: { id: 'leads.webhook.confirmRegenerate', defaultMessage: 'Confirm regenerate' },
  deleteWebhook: { id: 'leads.webhook.deleteWebhook', defaultMessage: 'Delete webhook' },
  deleteConfirm: { id: 'leads.webhook.deleteConfirm', defaultMessage: 'Delete this webhook config?' },
  yesDelete: { id: 'leads.webhook.yesDelete', defaultMessage: 'Yes, delete' },
  saving: { id: 'leads.webhook.saving', defaultMessage: 'Saving…' },
  updateWebhook: { id: 'leads.webhook.updateWebhook', defaultMessage: 'Update webhook' },
  createWebhook: { id: 'leads.webhook.createWebhook', defaultMessage: 'Create webhook' },
  errLoadConfig: { id: 'leads.webhook.errLoadConfig', defaultMessage: 'Error loading webhook config' },
  errNetworkLoad: { id: 'leads.webhook.errNetworkLoad', defaultMessage: 'Network error loading config' },
  selectLeadSourceWarn: { id: 'leads.webhook.selectLeadSourceWarn', defaultMessage: 'Please select a lead source' },
  selectCampusWarn: { id: 'leads.webhook.selectCampusWarn', defaultMessage: 'Please select a campus' },
  webhookUpdated: { id: 'leads.webhook.webhookUpdated', defaultMessage: 'Webhook updated' },
  webhookCreated: { id: 'leads.webhook.webhookCreated', defaultMessage: 'Webhook created' },
  errSaving: { id: 'leads.webhook.errSaving', defaultMessage: 'Error saving webhook' },
  errNetworkSave: { id: 'leads.webhook.errNetworkSave', defaultMessage: 'Network error saving webhook' },
  webhookDeleted: { id: 'leads.webhook.webhookDeleted', defaultMessage: 'Webhook deleted' },
  errDeleting: { id: 'leads.webhook.errDeleting', defaultMessage: 'Error deleting webhook' },
  errNetworkDelete: { id: 'leads.webhook.errNetworkDelete', defaultMessage: 'Network error deleting webhook' },
  toastRegenLovable: {
    id: 'leads.webhook.toastRegenLovable',
    defaultMessage: 'Token and secret regenerated — update your Lovable prompt/site',
  },
  errRegenToken: { id: 'leads.webhook.errRegenToken', defaultMessage: 'Error regenerating token' },
  errNetworkRegen: { id: 'leads.webhook.errNetworkRegen', defaultMessage: 'Network error regenerating token' },
  copyError: { id: 'leads.webhook.copyError', defaultMessage: 'Could not copy to clipboard' },
  promptDetailsHelp: {
    id: 'leads.webhook.promptDetailsHelp',
    defaultMessage: 'Copy this and paste it into your Lovable chat. It always matches the field mapping configured in the other tabs — if you change the mapping, come back here and copy it again.',
  },
  copyPrompt: { id: 'leads.webhook.copyPrompt', defaultMessage: 'Copy prompt' },
  savePromptHint: {
    id: 'leads.webhook.savePromptHint',
    defaultMessage: 'Save the webhook first to generate its URL and secret — the prompt will appear here once it exists.',
  },
})

export const ModalLovableLeadsWebhook: React.FC<Props> = ({
  isOpen,
  onClose,
  stage,
  configId,
  onChanged,
  canEdit,
}) => {
  const intl = useIntl()

  const stageNumericId = Number(parseId(stage.id))

  const [config, setConfig] = useState<WebhookConfig | null>(null)
  const [options, setOptions] = useState<Options | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [copied, setCopied] = useState<CopyField>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const [activeTab, setActiveTab] = useState('webhook')
  const [showTagManager, setShowTagManager] = useState(false)
  const [viewLeadInfo, setViewLeadInfo] = useState<IInfoLead | null>(null)

  // Form state
  const [categoryId, setCategoryId] = useState('')
  const [leadSourceId, setLeadSourceId] = useState('')
  const [campusId, setCampusId] = useState('')
  const [typeLead, setTypeLead] = useState<'PERSONAL' | 'COMERCIAL'>('PERSONAL')
  const [isActive, setIsActive] = useState(true)
  const [description, setDescription] = useState('')
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  // Field mapping — no platform-fixed defaults, since the Lovable form is user-authored
  const [nameField, setNameField] = useState('name')
  const [emailField, setEmailField] = useState('email')
  const [phoneField, setPhoneField] = useState('phone')
  const [metadataRows, setMetadataRows] = useState<MetadataRow[]>([])

  const loadConfig = useCallback(async () => {
    if (!configId) {
      setConfig(null)
      setActiveTab('field-mapping')
      return
    }
    setLoadingConfig(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/lovable-leads/webhook/config/${configId}/`, {
        headers: authHeaders(),
      })
      if (res.status === 404) {
        setConfig(null)
        setActiveTab('field-mapping')
      } else if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setActiveTab('webhook')
        setLeadSourceId(data.lead_source_id || '')
        setCampusId(data.campus_id || '')
        setTypeLead(data.type_lead)
        setIsActive(data.is_active)
        setDescription(data.description || '')
        setSelectedTagIds(data.tags.map((t) => t.id))
        setNameField(data.name_field || 'name')
        setEmailField(data.email_field || 'email')
        setPhoneField(data.phone_field || 'phone')
        setMetadataRows(metadataMapToRows(data.metadata_field_map))
      } else {
        toast.error(intl.formatMessage(wM.errLoadConfig))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkLoad))
    } finally {
      setLoadingConfig(false)
    }
  }, [configId, intl])

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/lovable-leads/webhook/options/`, {
        headers: authHeaders(),
      })
      if (res.ok) {
        const data: Options = await res.json()
        setOptions(data)
      }
    } catch {
      // non-blocking
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setConfig(null)
      setOptions(null)
      setCategoryId('')
      setLeadSourceId('')
      setCampusId('')
      setTypeLead('PERSONAL')
      setIsActive(true)
      setDescription('')
      setSelectedTagIds([])
      setNameField('name')
      setEmailField('email')
      setPhoneField('phone')
      setMetadataRows([])
      setConfirmDelete(false)
      setConfirmRegenerate(false)
      setActiveTab('field-mapping')
      return
    }
    loadConfig()
    loadOptions()
  }, [isOpen, loadConfig, loadOptions])

  // Resolve category from loaded config's lead_source_id
  useEffect(() => {
    if (!config?.lead_source_id || !options) return
    for (const cat of options.categories) {
      if (cat.sources.some((s) => s.id === config.lead_source_id)) {
        setCategoryId(cat.id)
        break
      }
    }
  }, [config, options])

  const filteredSources = options?.categories.find((c) => c.id === categoryId)?.sources || []

  const updateMetadataRow = (index: number, field: 'target' | 'source', value: string) => {
    setMetadataRows((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)))
  }

  const addMetadataRow = () => {
    setMetadataRows((prev) => [...prev, { target: '', source: '' }])
  }

  const removeMetadataRow = (index: number) => {
    setMetadataRows((prev) => prev.filter((_, i) => i !== index))
  }

  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) => (prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]))
  }

  const handleSave = async () => {
    if (!leadSourceId) {
      toast.warning(intl.formatMessage(wM.selectLeadSourceWarn))
      return
    }
    if (!campusId) {
      toast.warning(intl.formatMessage(wM.selectCampusWarn))
      return
    }
    setSaving(true)
    try {
      const existingId = config?.id ?? configId
      const url = existingId
        ? `${BACKEND_URL}/api/lovable-leads/webhook/config/${existingId}/`
        : `${BACKEND_URL}/api/lovable-leads/webhook/${stageNumericId}/`
      const res = await fetch(url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          lead_source_id: leadSourceId,
          campus_id: campusId,
          type_lead: typeLead,
          is_active: isActive,
          description: description.trim(),
          tag_ids: selectedTagIds,
          name_field: nameField.trim() || 'name',
          email_field: emailField.trim() || 'email',
          phone_field: phoneField.trim() || 'phone',
          metadata_field_map: metadataRowsToMap(metadataRows),
        }),
      })
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        const wasNew = !config
        setConfig(data)
        setSelectedTagIds(data.tags.map((t) => t.id))
        toast.success(wasNew ? intl.formatMessage(wM.webhookCreated) : intl.formatMessage(wM.webhookUpdated))
        if (wasNew) setActiveTab('webhook')
        onChanged?.()
      } else {
        const err = await res.json()
        toast.error(err?.error || intl.formatMessage(wM.errSaving))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkSave))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!configId) return
    setDeleting(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/lovable-leads/webhook/config/${configId}/delete/`,
        { method: 'DELETE', headers: authHeaders() }
      )
      if (res.ok) {
        setConfig(null)
        setConfirmDelete(false)
        toast.success(intl.formatMessage(wM.webhookDeleted))
        onChanged?.()
        onClose()
      } else {
        toast.error(intl.formatMessage(wM.errDeleting))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkDelete))
    } finally {
      setDeleting(false)
    }
  }

  const handleRegenerate = async () => {
    if (!configId) return
    setRegenerating(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/lovable-leads/webhook/config/${configId}/regenerate/`,
        { method: 'POST', headers: authHeaders() }
      )
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setConfirmRegenerate(false)
        toast.success(intl.formatMessage(wM.toastRegenLovable))
      } else {
        toast.error(intl.formatMessage(wM.errRegenToken))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkRegen))
    } finally {
      setRegenerating(false)
    }
  }

  const copyToClipboard = async (text: string, field: CopyField) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(field)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error(intl.formatMessage(wM.copyError))
    }
  }

  const isLoading = loadingConfig || loadingOptions

  const prompt = config
    ? buildPrompt({
        webhookUrl: config.webhook_url,
        webhookSecret: config.webhook_secret,
        nameField,
        emailField,
        phoneField,
        metadataRows,
      })
    : ''

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      size='lg'
      centered
      className='webhook-modal-dialog'
      backdropClassName='webhook-modal-backdrop'
    >
      <Modal.Header closeButton>
        <div className='d-flex align-items-center justify-content-between w-100 me-2'>
          <Modal.Title className='d-flex align-items-center gap-2'>
            {getLeadSourceIcon(config?.lead_source_name) ? (
              <i
                className={`bi ${getLeadSourceIcon(config?.lead_source_name)} text-success`}
                style={{ fontSize: '1.3rem' }}
              />
            ) : (
              <LovableIcon size={22} />
            )}
            {intl.formatMessage(wM.lovableTitle)} {emdash} {stage.name}
          </Modal.Title>
          {config && (
            <Form.Check
              type='switch'
              id='header-webhook-active-toggle'
              label={
                <span className='fw-semibold' style={{ fontSize: '0.85rem' }}>
                  {intl.formatMessage(wM.webhookActive)}
                </span>
              }
              checked={isActive}
              disabled={!canEdit}
              onChange={(e) => setIsActive(e.target.checked)}
              className='mb-0'
            />
          )}
        </div>
      </Modal.Header>

      <Modal.Body className='p-0'>
        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner animation='border' />
          </div>
        ) : (
          <>
            <div className='px-3 pt-3'>
              {!canEdit && (
                <Alert variant='secondary' className='py-2 mb-3'>
                  <i className='bi bi-eye me-2' />
                  <small>{intl.formatMessage(wM.readOnly)}</small>
                </Alert>
              )}
              <Form.Group className='mb-0'>
                <Form.Label className='fw-bold'>{intl.formatMessage(wM.description)}</Form.Label>
                <Form.Control
                  type='text'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={intl.formatMessage(wM.descriptionPlaceholder)}
                  disabled={!canEdit}
                />
              </Form.Group>
            </div>
            <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || 'webhook')}>
              {/* Tab navigation */}
              <div className='border-bottom px-3 pt-3'>
                <Nav variant='tabs' className='border-bottom-0'>
                  <Nav.Item>
                    <Nav.Link eventKey='webhook'>
                      <i className='bi bi-link-45deg me-1' />
                      {intl.formatMessage(wM.tabWebhook)}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey='field-mapping'>
                      <i className='bi bi-diagram-3 me-1' />
                      {intl.formatMessage(wM.tabFieldMapping)}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey='extra-fields'>
                      <i className='bi bi-tags me-1' />
                      {intl.formatMessage(wM.tabExtraFields)}
                      {metadataRows.filter(r => r.target.trim() && r.source.trim()).length > 0 && (
                        <Badge bg='primary' pill className='ms-1' style={{ fontSize: '0.65rem' }}>
                          {metadataRows.filter(r => r.target.trim() && r.source.trim()).length}
                        </Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey='prompt-details'>
                      <i className='bi bi-code-slash me-1' />
                      {intl.formatMessage(wM.tabPromptDetails)}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </div>

              <Tab.Content>
                {/* ── Tab 1: Webhook ── */}
                <Tab.Pane eventKey='webhook' className='p-3'>

                  {/* Webhook info (only when configured) */}
                  {config && (
                    <div className='mb-4'>
                      <div className='d-flex align-items-center gap-2 mb-3'>
                        <Badge bg='success' className='fs-6'>
                          <i className='bi bi-check-circle me-1' />
                          {intl.formatMessage(wM.configured)}
                        </Badge>
                        <Badge bg={config.is_active ? 'success' : 'secondary'}>
                          {config.is_active
                            ? intl.formatMessage(wM.statusActive)
                            : intl.formatMessage(wM.statusInactive)}
                        </Badge>
                      </div>

                      <Alert variant='info' className='py-2 mb-3'>
                        <small>
                          {intl.formatMessage(wM.lovableInstructions, { b: (chunks: (string | JSX.Element)[]) => <strong>{chunks}</strong> })}
                        </small>
                      </Alert>

                      {/* Webhook URL */}
                      <Form.Group className='mb-3'>
                        <Form.Label className='fw-bold'>{intl.formatMessage(wM.webhookUrl)}</Form.Label>
                        <div className='input-group'>
                          <Form.Control
                            type='text'
                            readOnly
                            value={config.webhook_url}
                            className='font-monospace'
                            style={{ fontSize: '0.8rem' }}
                          />
                          <Button
                            variant={copied === 'url' ? 'success' : 'outline-secondary'}
                            onClick={() => copyToClipboard(config.webhook_url, 'url')}
                          >
                            <i className={`bi ${copied === 'url' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                          </Button>
                        </div>
                      </Form.Group>

                      {/* Webhook Secret */}
                      <Form.Group className='mb-4'>
                        <Form.Label className='fw-bold'>{intl.formatMessage(wM.webhookSecret)}</Form.Label>
                        <div className='input-group'>
                          <Form.Control
                            type='text'
                            readOnly
                            value={config.webhook_secret}
                            className='font-monospace'
                          />
                          <Button
                            variant={copied === 'secret' ? 'success' : 'outline-secondary'}
                            onClick={() => copyToClipboard(config.webhook_secret, 'secret')}
                          >
                            <i className={`bi ${copied === 'secret' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                          </Button>
                        </div>
                        <Form.Text className='text-muted'>
                          {intl.formatMessage(wM.lovableSecretHint, { code: (chunks: (string | JSX.Element)[]) => <code>{chunks}</code> })}
                        </Form.Text>
                      </Form.Group>

                      {/* Stats */}
                      <div className='d-flex gap-3 mb-4'>
                        <div className='border rounded p-2 flex-fill text-center'>
                          <div className='fw-bold fs-4'>{config.leads_count}</div>
                          <small className='text-muted'>{intl.formatMessage(wM.leadsReceived)}</small>
                        </div>
                        <div className='border rounded p-2 flex-fill text-center'>
                          <div className='fw-bold d-flex align-items-center justify-content-center gap-1' style={{ fontSize: '0.85rem' }}>
                            {config.last_triggered_at
                              ? new Date(config.last_triggered_at).toLocaleString()
                              : emdash}
                            {config.last_lead_id && (
                              <Button
                                variant='link'
                                size='sm'
                                className='p-0'
                                title={intl.formatMessage(wM.viewLastLead)}
                                onClick={() => setViewLeadInfo({ id: String(config.last_lead_id), name: 'Lead' })}
                              >
                                <i className='bi bi-box-arrow-up-right' />
                              </Button>
                            )}
                          </div>
                          <small className='text-muted'>{intl.formatMessage(wM.lastTriggered)}</small>
                        </div>
                        <div className='border rounded p-2 flex-fill text-center'>
                          <Badge bg={config.is_active ? 'success' : 'secondary'} className='fs-6'>
                            {config.is_active ? intl.formatMessage({ id: 'common.status.active', defaultMessage: 'Active' }) : intl.formatMessage({ id: 'common.status.inactive', defaultMessage: 'Inactive' })}
                          </Badge>
                          <br />
                          <small className='text-muted'>{intl.formatMessage(wM.statusLabel)}</small>
                        </div>
                      </div>

                      <hr />
                    </div>
                  )}


                  {/* Danger zone — regenerar y borrar exigen `change_`/`delete_` */}
                  {config && canEdit && (
                    <div className='border border-warning rounded p-3 mt-4'>
                      <p className='fw-bold mb-2 text-warning'>
                        <i className='bi bi-exclamation-triangle me-2' />
                        {intl.formatMessage(wM.dangerZone)}
                      </p>

                      {!confirmRegenerate ? (
                        <Button
                          variant='outline-warning'
                          size='sm'
                          className='me-2'
                          onClick={() => setConfirmRegenerate(true)}
                        >
                          <i className='bi bi-arrow-repeat me-1' />
                          {intl.formatMessage(wM.regenTokenSecret)}
                        </Button>
                      ) : (
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <small className='text-warning'>{intl.formatMessage(wM.regenWarnLovable)}</small>
                          <Button
                            variant='warning'
                            size='sm'
                            onClick={handleRegenerate}
                            disabled={regenerating}
                          >
                            {regenerating ? <Spinner size='sm' animation='border' /> : intl.formatMessage(wM.confirmRegenerate)}
                          </Button>
                          <Button variant='link' size='sm' onClick={() => setConfirmRegenerate(false)}>
                            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                          </Button>
                        </div>
                      )}

                      {!confirmDelete ? (
                        <Button
                          variant='outline-danger'
                          size='sm'
                          className='mt-2'
                          onClick={() => setConfirmDelete(true)}
                        >
                          <i className='bi bi-trash me-1' />
                          {intl.formatMessage(wM.deleteWebhook)}
                        </Button>
                      ) : (
                        <div className='d-flex align-items-center gap-2 mt-2'>
                          <small className='text-danger'>{intl.formatMessage(wM.deleteConfirm)}</small>
                          <Button variant='danger' size='sm' onClick={handleDelete} disabled={deleting}>
                            {deleting ? <Spinner size='sm' animation='border' /> : intl.formatMessage(wM.yesDelete)}
                          </Button>
                          <Button variant='link' size='sm' onClick={() => setConfirmDelete(false)}>
                            <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </Tab.Pane>

                {/* ── Tab 2: Field Mapping ── */}
                <Tab.Pane eventKey='field-mapping' className='p-3'>
                  <h6 className='mb-2'>
                    <i className='bi bi-diagram-3 me-2' />
                    {intl.formatMessage(wM.tabFieldMapping)}
                  </h6>
                  <p className='text-muted small mb-3'>
                    {intl.formatMessage(wM.fieldMappingDescLovable)}
                  </p>
                  <div className='row g-3'>
                    <div className='col-md-4'>
                      <Form.Group>
                        <Form.Label><FormattedMessage id='common.field.name' defaultMessage='Name' /></Form.Label>
                        <Form.Control
                          type='text'
                          placeholder='name'
                          value={nameField}
                          disabled={!canEdit}
                          onChange={(e) => setNameField(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-4'>
                      <Form.Group>
                        <Form.Label><FormattedMessage id='common.field.email' defaultMessage='Email' /></Form.Label>
                        <Form.Control
                          type='text'
                          placeholder='email'
                          value={emailField}
                          disabled={!canEdit}
                          onChange={(e) => setEmailField(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                    <div className='col-md-4'>
                      <Form.Group>
                        <Form.Label><FormattedMessage id='common.field.phone' defaultMessage='Phone' /></Form.Label>
                        <Form.Control
                          type='text'
                          placeholder='phone'
                          value={phoneField}
                          disabled={!canEdit}
                          onChange={(e) => setPhoneField(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </div>

                  <hr className='my-4' />

                  {/* ── Update Settings ── */}
                  <h6 className='mb-3'>{config ? intl.formatMessage(wM.updateSettings) : intl.formatMessage(wM.createForStage)}</h6>

                  <div className='row g-3'>
                    {/* ── Left column: Lead Fields ── */}
                    <div className='col-md-6'>
                      <p className='text-muted small fw-semibold mb-2' style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                        <i className='bi bi-person-lines-fill me-1' />
                        {intl.formatMessage(wM.colLeadFields)}
                      </p>

                      {/* Lead Type */}
                      <Form.Group className='mb-3'>
                        <Form.Label>{intl.formatMessage(wM.leadType)}</Form.Label>
                        <div className='d-flex gap-3'>
                          <Form.Check
                            type='radio'
                            label={intl.formatMessage({ id: 'leads.form.typePersonal', defaultMessage: 'Personal' })}
                            id='lv-type-personal'
                            checked={typeLead === 'PERSONAL'}
                            disabled={!canEdit}
                            onChange={() => setTypeLead('PERSONAL')}
                          />
                          <Form.Check
                            type='radio'
                            label={intl.formatMessage({ id: 'leads.form.typeCommercial', defaultMessage: 'Commercial' })}
                            id='lv-type-comercial'
                            checked={typeLead === 'COMERCIAL'}
                            disabled={!canEdit}
                            onChange={() => setTypeLead('COMERCIAL')}
                          />
                        </div>
                      </Form.Group>

                      {/* Source Category */}
                      <Form.Group className='mb-3'>
                        <Form.Label>{intl.formatMessage(wM.leadSourceCategory)}</Form.Label>
                        <Form.Select
                          value={categoryId}
                          onChange={(e) => {
                            setCategoryId(e.target.value)
                            setLeadSourceId('')
                          }}
                          disabled={loadingOptions || !canEdit}
                        >
                          <option value=''>{intl.formatMessage(wM.selectCategory)}</option>
                          {options?.categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      {/* Lead Source */}
                      <Form.Group className='mb-3'>
                        <Form.Label><FormattedMessage id='leads.form.leadSource' defaultMessage='Lead source' /></Form.Label>
                        <Form.Select
                          value={leadSourceId}
                          onChange={(e) => setLeadSourceId(e.target.value)}
                          disabled={!categoryId || loadingOptions || !canEdit}
                        >
                          <option value=''>{intl.formatMessage(wM.selectSource)}</option>
                          {filteredSources.map((s) => (
                            <option key={s.id} value={s.id}>
                              {s.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </div>

                    {/* ── Right column: Other ── */}
                    <div className='col-md-6'>
                      <p className='text-muted small fw-semibold mb-2' style={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                        <i className='bi bi-sliders me-1' />
                        {intl.formatMessage(wM.colOther)}
                      </p>

                      {/* Campus */}
                      <Form.Group className='mb-3'>
                        <Form.Label>{intl.formatMessage(wM.campus)}</Form.Label>
                        <Form.Select
                          value={campusId}
                          onChange={(e) => setCampusId(e.target.value)}
                          disabled={loadingOptions || !canEdit}
                        >
                          <option value=''>{intl.formatMessage(wM.selectCampus)}</option>
                          {options?.campuses.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>

                      {/* Tags */}
                      <Form.Group className='mb-3'>
                        <div className='d-flex align-items-center justify-content-between'>
                          <Form.Label className='mb-0'>{intl.formatMessage(wM.autoAssignTags)}</Form.Label>
                          {canEdit && (
                            <Button
                              variant='link'
                              size='sm'
                              className='p-0'
                              onClick={() => setShowTagManager(true)}
                            >
                              <i className='bi bi-tags me-1' />
                              {intl.formatMessage(wM.manageTags)}
                            </Button>
                          )}
                        </div>
                        {options && options.tags.length > 0 && (
                          <div className='d-flex flex-wrap gap-2 p-2 border rounded mt-1'>
                            {options.tags.map((tag) => {
                              const selected = selectedTagIds.includes(tag.id)
                              return (
                                <button
                                  key={tag.id}
                                  type='button'
                                  disabled={!canEdit}
                                  onClick={() => toggleTag(tag.id)}
                                  style={{
                                    background: selected ? (tag.color || '#6c757d') : 'transparent',
                                    border: `2px solid ${tag.color || '#6c757d'}`,
                                    borderRadius: '20px',
                                    padding: '3px 12px',
                                    fontSize: '0.82rem',
                                    fontWeight: selected ? 600 : 400,
                                    color: selected ? '#fff' : (tag.color || '#6c757d'),
                                    cursor: 'pointer',
                                    transition: 'all 0.15s',
                                  }}
                                >
                                  {selected && <i className='bi bi-check me-1' />}
                                  {tag.name}
                                </button>
                              )
                            })}
                          </div>
                        )}
                        {selectedTagIds.length > 0 && (
                          <small className='text-muted mt-1 d-block'>{intl.formatMessage(wM.tagsSelected, { count: selectedTagIds.length })}</small>
                        )}
                      </Form.Group>
                    </div>
                  </div>
                </Tab.Pane>

                {/* ── Tab 3: Extra Field Mapping ── */}
                <Tab.Pane eventKey='extra-fields' className='p-3'>
                  <Form.Group className='mb-4'>
                    <Form.Label className='fw-bold'>
                      <i className='bi bi-tags me-2' />
                      {intl.formatMessage(wM.extraMetadata)}
                    </Form.Label>
                    <p className='text-muted small mb-2'>
                      {intl.formatMessage(wM.extraMetadataHelpLovable, {
                        b: (chunks: (string | JSX.Element)[]) => <strong>{chunks}</strong>,
                      })}
                    </p>
                    <div className='mb-2'>
                      {/* Counter badge */}
                      {metadataRows.length > 0 && (
                        <div className='d-flex align-items-center gap-2 mb-2'>
                          <span className='badge bg-primary rounded-pill'>
                            <i className='bi bi-check2-circle me-1' />
                            {intl.formatMessage(wM.fieldsConfigured, {
                              configured: metadataRows.filter((r) => r.target.trim() && r.source.trim())
                                .length,
                              total: metadataRows.length,
                            })}
                          </span>
                          {metadataRows.filter(r => !r.target.trim() || !r.source.trim()).length > 0 && (
                            <span className='badge bg-warning text-dark rounded-pill'>
                              <i className='bi bi-exclamation-circle me-1' />
                              {intl.formatMessage(wM.fieldsIncomplete, {
                                count: metadataRows.filter((r) => !r.target.trim() || !r.source.trim())
                                  .length,
                              })}
                            </span>
                          )}
                        </div>
                      )}

                      {/* Scrollable rows */}
                      <div
                        style={{
                          maxHeight: '260px',
                          overflowY: 'auto',
                          paddingRight: '4px',
                        }}
                      >
                        {metadataRows.map((row, index) => (
                          <div className='row g-2 mb-2 align-items-center' key={index}>
                            <div className='col-md-5'>
                              <Form.Control
                                type='text'
                                placeholder={intl.formatMessage(wM.internalNamePlaceholder)}
                                value={row.target}
                                disabled={!canEdit}
                                onChange={(e) => updateMetadataRow(index, 'target', e.target.value)}
                              />
                            </div>
                            <div className='col-md-5'>
                              <Form.Control
                                type='text'
                                placeholder={intl.formatMessage(wM.payloadKeyPlaceholder)}
                                value={row.source}
                                disabled={!canEdit}
                                onChange={(e) => updateMetadataRow(index, 'source', e.target.value)}
                              />
                            </div>
                            <div className='col-md-2'>
                              <Button
                                variant='outline-danger'
                                size='sm'
                                onClick={() => removeMetadataRow(index)}
                                disabled={!canEdit}
                                title={intl.formatMessage(wM.removeField)}
                              >
                                <i className='bi bi-x-lg' />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <Alert variant='warning' className='py-2 mt-2 mb-2'>
                        <small>
                          <i className='bi bi-exclamation-triangle me-1' />
                          {intl.formatMessage(wM.extraWarning, {
                            code: (chunks: (string | JSX.Element)[]) => <code>{chunks}</code>,
                          })}
                        </small>
                      </Alert>

                      <Button
                        variant='outline-secondary'
                        size='sm'
                        onClick={addMetadataRow}
                        disabled={!canEdit}
                        className='mt-1'
                      >
                        <i className='bi bi-plus-lg me-1' />
                        {intl.formatMessage(wM.addField)}
                      </Button>
                    </div>
                  </Form.Group>
                </Tab.Pane>

                {/* ── Tab 4: Prompt Details ── */}
                <Tab.Pane eventKey='prompt-details' className='p-3'>
                  <h6 className='mb-2'>
                    <i className='bi bi-code-slash me-2' />
                    {intl.formatMessage(wM.tabPromptDetails)}
                  </h6>
                  {config ? (
                    <>
                      <p className='text-muted small mb-3'>
                        {intl.formatMessage(wM.promptDetailsHelp)}
                      </p>
                      <div className='position-relative'>
                        <pre
                          className='bg-light border rounded p-3 font-monospace'
                          style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {prompt}
                        </pre>
                        <Button
                          variant={copied === 'prompt' ? 'success' : 'outline-secondary'}
                          size='sm'
                          className='position-absolute top-0 end-0 m-2'
                          onClick={() => copyToClipboard(prompt, 'prompt')}
                        >
                          <i className={`bi ${copied === 'prompt' ? 'bi-check-lg' : 'bi-clipboard'} me-1`} />
                          {intl.formatMessage(wM.copyPrompt)}
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Alert variant='secondary' className='py-2'>
                      <small>{intl.formatMessage(wM.savePromptHint)}</small>
                    </Alert>
                  )}
                </Tab.Pane>
              </Tab.Content>
            </Tab.Container>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.close' defaultMessage='Close' />
        </Button>
        {!isLoading && canEdit && (
          <Button variant='primary' onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size='sm' animation='border' className='me-2' />
                {intl.formatMessage(wM.saving)}
              </>
            ) : config ? (
              intl.formatMessage(wM.updateWebhook)
            ) : (
              intl.formatMessage(wM.createWebhook)
            )}
          </Button>
        )}
      </Modal.Footer>

      <LeadTagManager
        show={showTagManager}
        onHide={() => setShowTagManager(false)}
        onTagsUpdated={loadOptions}
      />

      {viewLeadInfo && (
        <DrawerEditLead
          isOpen={!!viewLeadInfo}
          onClose={() => setViewLeadInfo(null)}
          info={viewLeadInfo}
          updateListLeads={() => { }}
        />
      )}
    </Modal>
  )
}
